import * as PIXI from "pixi.js";

import { assetManifest } from "@src/game/assets/AssetImports";
import { Assets, TAssetData } from "@src/core/assets/Assets";
import { EventSystem } from "@src/core/events/EventSystem";
import { Screen } from "@src/core/screen/Screen";
import { constants } from "@src/game/Constants";
import { Spine } from "@pixi/spine-pixi";

// Workaround to get asset name completion to work properly
type TLoadingAssetAlias = keyof typeof assetManifest &
  (
    | `${string}loading${string}`
    | `${string}Loading${string}`
    | `loading${string}`
    | `Loading${string}`
    | `${string}font${string}`
    | `${string}Font${string}`
  );

export class LoadingScreen extends Screen {
  public get assets(): Record<TLoadingAssetAlias, TAssetData> {
    return Object.fromEntries(
      Object.entries(assetManifest).filter(
        ([alias, asset]) =>
          alias.toLowerCase().includes("loading") ||
          alias.toLowerCase().includes("font") ||
          alias.toLowerCase().includes("logo") ||
          alias.includes("sndIdle") ||
          asset.bundle === constants.Bundles.FONTS,
      ),
    ) as Record<TLoadingAssetAlias, TAssetData>;
  }

  private _backgroundHorizontal!: PIXI.Sprite;
  private _logo!: Spine;
  private _panelBonus!: PIXI.Sprite;
  private _wildPirate!: PIXI.Sprite;
  private _loadingBar!: PIXI.Sprite;
  private _loadingBarFiller!: PIXI.Sprite;
  private _loadingBarMask!: PIXI.Graphics;
  private _wildPirateText!: PIXI.Text;
  private _bonusPanelText!: PIXI.Text;
  private _tapToContinueText!: PIXI.Text;

  public setProgress(progress: number): void {
    this._loadingBarMask.width = this._loadingBarFiller.width * progress;
  }

  public showContinueMessage(): void {
    this._loadingBarFiller.visible = false;
    this._loadingBar.visible = false;
    this._tapToContinueText.visible = true;
  }

  protected onLoadedAssets(): void {
    super.onLoadedAssets();

    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequested.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequested.bind(this),
    );
  }

  private _initializeAssets(): void {
    this._backgroundHorizontal = new PIXI.Sprite(
      Assets.getAsset<TLoadingAssetAlias>("loadingBgHorizontalImg"),
    );
    this._backgroundHorizontal.anchor.set(0.5);
    this._backgroundHorizontal.position.set(0);

    const loadingAtlasSpritesheet =
      Assets.getAsset<TLoadingAssetAlias>("atlasLoading");

    this._logo = Spine.from({ skeleton: "logoAtlasJson", atlas: "logoAtlas" });
    this._logo.skeleton.setSkinByName("default");
    this._logo.state.setAnimation(0, "idle", true);

    this._panelBonus = new PIXI.Sprite(
      loadingAtlasSpritesheet.textures["bonus_panel.webp"],
    );
    this._panelBonus.anchor.set(0.5);

    this._wildPirate = new PIXI.Sprite(
      loadingAtlasSpritesheet.textures["wild_pirate.webp"],
    );
    this._wildPirate.anchor.set(0.5);

    this._loadingBar = new PIXI.Sprite(
      loadingAtlasSpritesheet.textures["loading_bar.webp"],
    );
    this._loadingBar.anchor.set(0.5);

    this._loadingBarFiller = new PIXI.Sprite(
      loadingAtlasSpritesheet.textures["loading_bar_filler.webp"],
    );
    this._loadingBarFiller.anchor.set(0.5);

    this._loadingBarMask = new PIXI.Graphics();
    this._loadingBarMask
      .rect(0, 0, this._loadingBarFiller.width, this._loadingBarFiller.height)
      .fill({ color: 0xffffff, alpha: 1 });

    this._loadingBarMask.width = 0;
    this._loadingBarFiller.mask = this._loadingBarMask;
    this._wildPirateText = new PIXI.Text({
      text: "wilds always expand during free spins",
      style: {
        fontSize: 30,
        fontFamily: "gameFont",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 378,
        fill: 0xffbb1e,
        stroke: {
          color: 0x000000,
          width: 7,
          join: "round",
        },
        letterSpacing: 0,
        fontStyle: "italic",
        lineHeight: 33,
        padding: 4,
      },
    });
    this._wildPirateText.anchor.set(0.5);

    this._bonusPanelText = new PIXI.Text({
      text: "fill the full panel in bonus game to get the grand prize!",
      style: {
        fontSize: 35,
        fontFamily: "gameFont",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 480,
        fill: 0xffbb1e,
        stroke: {
          color: 0x000000,
          width: 7,
          join: "round",
        },
        letterSpacing: 0,
        fontStyle: "italic",
        lineHeight: 38,
        padding: 4,
      },
    });
    this._bonusPanelText.anchor.set(0.5);

    this._tapToContinueText = new PIXI.Text({
      text: "tap anywhere to continue",
      style: {
        fontSize: 28,
        fontFamily: "gameFont",
        align: "center",
        wordWrap: true,
        wordWrapWidth: 700,
        fill: 0xffbb1e,
        stroke: {
          color: 0x000000,
          width: 7,
          join: "round",
        },
        letterSpacing: 0,
        lineHeight: 38,
        padding: 4,
      },
    });
    this._tapToContinueText.anchor.set(0.5);
    this._tapToContinueText.visible = false;
  }

  private _parentAssets(): void {
    this.view.addChild(this._backgroundHorizontal);
    this.view.addChild(this._logo);
    this.view.addChild(this._panelBonus);
    this.view.addChild(this._wildPirate);
    this.view.addChild(this._loadingBar);
    this.view.addChild(this._loadingBarFiller);
    this.view.addChild(this._loadingBarMask);
    this.view.addChild(this._wildPirateText);
    this.view.addChild(this._bonusPanelText);
    this.view.addChild(this._tapToContinueText);
  }

  private _onLandscapeLayoutRequested(): void {
    this._backgroundHorizontal.scale.set(1.75);

    this._logo.scale.set(0.11);
    this._logo.position.set(0, -400);

    this._panelBonus.scale.set(0.4);
    this._panelBonus.position.set(500, -40);

    this._wildPirate.scale.set(0.4);
    this._wildPirate.position.set(-500, 0);

    this._loadingBar.position.set(0, 370);
    this._loadingBarFiller.position.set(0, 370);

    this._loadingBarMask.x =
      this._loadingBarFiller.x - this._loadingBarFiller.width / 2;
    this._loadingBarMask.y =
      this._loadingBarFiller.y - this._loadingBarFiller.height / 2;

    this._wildPirateText.position.set(-640, 130);
    this._bonusPanelText.position.set(500, 255);
    this._tapToContinueText.position.set(0, 370);
  }

  private _onPortraitLayoutRequested(): void {
    this._backgroundHorizontal.scale.set(2.75);

    this._logo.scale.set(0.15);
    this._logo.position.set(0, -750);

    this._panelBonus.scale.set(0.4);
    this._panelBonus.position.set(0, 400);

    this._wildPirate.scale.set(0.4);
    this._wildPirate.position.set(0, -300);

    this._loadingBar.position.set(0, 800);
    this._loadingBarFiller.position.set(0, 800);

    this._loadingBarMask.x =
      this._loadingBarFiller.x - this._loadingBarFiller.width / 2;
    this._loadingBarMask.y =
      this._loadingBarFiller.y - this._loadingBarFiller.height / 2;

    this._wildPirateText.position.set(-150, -150);
    this._bonusPanelText.position.set(0, 700);
    this._tapToContinueText.position.set(0, 800);
  }
}
